import { Form, Formik, FormikProps } from 'formik'
import React, { useRef } from 'react'
import {
  ContainerDiv,
  FjFormItem,
  FjRadio,
  FjRadioGroup,
  FjText,
  FormActionButtons,
  FormHeaderText,
} from 'src/components/Common'
import { Colors } from 'src/constants/colors'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { pluralize } from 'src/utils/format'

export type IPreserveCompletionModalProps = {
  contentTypeName: string
  onOptionSelected: (preserveCompletedProgress: boolean) => void
  onCancel: () => void
  count?: number
}

export const PreserveCompletionModal = ({
  contentTypeName,
  onOptionSelected,
  count = 1,
}: IPreserveCompletionModalProps) => {
  const formRef = useRef<FormikProps<any>>(null)

  const demonstrative = count > 1 ? 'these' : 'this'
  const pluralizedContentTypeName = pluralize(contentTypeName, count)

  const handleSubmit = ({ preserveCompletedProgress }) => {
    if (!preserveCompletedProgress) {
      sharedAppStateStore.sharedConfirmDialogProps = {
        onConfirm: () => onOptionSelected(preserveCompletedProgress),
        onCancel: () => (sharedAppStateStore.sharedConfirmDialogProps = undefined),
        content: `This will impact all users progress for ${demonstrative} ${pluralizedContentTypeName}.`,
        confirmButtonTitle: 'Confirm',
      }
      return
    }
    onOptionSelected(preserveCompletedProgress)
  }

  return (
    <ContainerDiv textAlign="left">
      <FormHeaderText heading="Update Current Progresses" />
      <FjText color={Colors.shark}>
        {`You have made changes to ${demonstrative} ${pluralizedContentTypeName}. Choose how you would like to handle existing progress records:`}
      </FjText>
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        initialValues={{ preserveCompletedProgress: true }}
        enableReinitialize
      >
        <Form>
          <FjFormItem name="preserveCompletedProgress">
            <FjRadioGroup name="preserveCompletedProgress">
              <ContainerDiv display="flex" flexDirection="column" color={Colors.shark}>
                <FjRadio name="preserveCompletedProgress" value={true}>
                  <ContainerDiv display="flex" flexDirection="column" marginTop="24px">
                    <FjText fontWeight="bold" textAlign="left">
                      Preserve Progress:
                    </FjText>
                    <FjText textAlign="left">
                      {`Users who have already completed ${demonstrative} ${pluralizedContentTypeName} will retain their completion status. New content will not affect their progress.`}
                    </FjText>
                  </ContainerDiv>
                </FjRadio>
                <FjRadio name="preserveCompletedProgress" value={false}>
                  <ContainerDiv display="flex" flexDirection="column" textAlign="left" marginTop="24px">
                    <FjText fontWeight="bold" textAlign="left">
                      Recalculate Progress:
                    </FjText>
                    <FjText textAlign="left">
                      {`Progress will be updated dynamically. Users may need to complete newly added content to regain their completion status.`}
                    </FjText>
                  </ContainerDiv>
                </FjRadio>
              </ContainerDiv>
            </FjRadioGroup>
          </FjFormItem>
          <FjText textAlign="left">
            {`Note: This only applies to users who have already completed ${demonstrative} ${pluralizedContentTypeName}.`}
          </FjText>
          <FormActionButtons submitButtonLabel="Save" />
        </Form>
      </Formik>
    </ContainerDiv>
  )
}
